// CPlayerSelectionController — 玩家手牌「點兩下出牌（預設）／單擊出牌」＋升起動畫互動
// 從 game-scene.ts 抽出，把出牌的小動畫流程集中管理。
//
// 點兩下（預設，PRD-Freemium §9 防誤觸）：第一下把那張牌升起並放大預覽（牌仍在手上），
// 再點同一張或點放大的那張才打出；改點別張就換成預覽那張。設定可改回單擊。
// 同一次點擊可能從 Phaser 與原生 pointer 補救兩條路徑各觸發一次，同一張牌 DUPLICATE_TAP_MS 內視為同一下。
//
// 出牌流程（單擊，或點兩下的第二下）：
//   點手牌 → 該張立刻從手牌排除、在原位生成一張往上升的放大牌（動畫）
//          → 停留 DISCARD_HOLD_MS（約 1 秒，可調）
//          → 放大牌 fade out，同時觸發實際出牌（等待反應或進海底由 scene 處理）
//   動畫進行中（pendingDiscardTile != null）忽略其他點擊；回合被打斷時由 clear() 收尾。
//
// === 使用圖片 ===
// 無 — 升起的牌用 CTileSprite（face = HandBottom），圖片清單見其檔頭

import type Phaser from 'phaser';

import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';
import type { TTile } from '@core/tile';
import type { TSeat } from '@core/types';
import { type CHandView, CTileSprite, TileFace, UIDepth } from '@ui/index';

import { SEAT_LAYOUT } from './seat-layout';

// ---- 升起動畫常數 ----
const RISE_OFFSET_Y = -110; // 升起終點相對手牌的 Y 位移（負 = 往上）
const RISE_SCALE = 0.85; // 升起後的放大縮放（略大於本家手牌 handScale）
const RISE_DURATION_MS = 100; // 升起動畫時長
const DISCARD_HOLD_MS = 300; // 升起後停留多久才出牌（「等個 1 秒」，可調）
const FADE_OUT_MS = 250; // 放大牌淡出時長
const RISE_EASE = 'Quad.easeOut'; // 升起緩動
const RISE_DEPTH = UIDepth.PlayerRiseTile; // 高於莊家 icon 52、風字徽章 -50，不被蓋住；與摸牌動畫一致

// ---- 點兩下：第一下的預覽 ----
const DUPLICATE_TAP_MS = 250; // 同一張牌這麼短時間內的重複觸發視為同一下
const PRIME_OFFSET_Y = -96; // 預覽牌相對手牌的 Y 位移
const PRIME_SCALE = 1.05; // 預覽牌放大（比升起出牌更大，長輩看得清楚）
const PRIME_HINT_OFFSET_Y = -82; // 「再點一下打出」相對預覽牌中心
const PRIME_HINT_FONT = '24px';

export type TPlayerSelectionDeps = {
  readonly scene: Phaser.Scene;
  readonly playerSeat: TSeat;
  readonly handViews: readonly CHandView[];
  /** 玩家當下是否可以挑牌出（phase / turn / proxy / preview 等規則）。 */
  readonly canSelect: () => boolean;
  /** 玩家剛選好牌（點下手牌的瞬間）→ 讓 scene 淡出回合手勢等，再開始升起動畫。 */
  readonly onPickTile?: () => void;
  /** 出牌動畫開始 / 結束後請 scene 重畫玩家手牌（依 getSelectedTile 決定要不要排除那張）。 */
  readonly onSelectionChange: () => void;
  /** 升起牌停留結束 → 觸發實際出牌；scene 決定走真實 round.submitAction 還是 preview mock。 */
  readonly onConfirm: (tile: TTile) => void;
  /** 是否點兩下才出牌（設定）；省略為單擊。 */
  readonly isDoubleTap?: () => boolean;
};

/**
 * 玩家自家手牌的「單擊出牌 + 升起動畫」互動 controller。
 * Scene 在 setupUI 之後建立一個實例，把 CHandView 的 onTileClick 接到 handleTileClick；
 * 並透過 getSelectedTile() 讓 scene 在重畫手牌時把「正在出的那張」排除。
 */
export class CPlayerSelectionController {
  private pendingDiscardTile: TTile | null = null;
  private riseSprite: CTileSprite | null = null;
  private holdTimer: Phaser.Time.TimerEvent | null = null;
  private primedTile: TTile | null = null;
  private primeObjects: Phaser.GameObjects.GameObject[] = [];
  private lastTap: { id: number; at: number } | null = null;

  constructor(private readonly deps: TPlayerSelectionDeps) {}

  /** 動畫進行中（出牌中）的那張牌；scene 用以把它從手牌排除渲染、並隱藏 action bar。 */
  getSelectedTile(): TTile | null {
    return this.pendingDiscardTile;
  }

  /** CHandView onTileClick callback：單擊即出牌（含升起動畫）。 */
  handleTileClick(tile: TTile): void {
    if (!this.deps.canSelect()) return;
    if (this.pendingDiscardTile) return; // 動畫進行中，忽略後續點擊
    const now = performance.now();
    if (this.lastTap?.id === tile.id && now - this.lastTap.at < DUPLICATE_TAP_MS) return;
    this.lastTap = { id: tile.id, at: now };
    if (this.deps.isDoubleTap?.() && this.primedTile?.id !== tile.id) {
      this.prime(tile);
      return;
    }
    this.clearPrime();

    // 1. 先取得這張牌目前畫面位置（排除前先取，排除後就找不到了）
    const pos =
      this.deps.handViews[this.deps.playerSeat]?.getConcealedTilePosition(tile.id) ?? null;

    // 2. 牌選好了 → 先淡出回合手勢（消失後才進入捨牌等待／海底流程）
    this.deps.onPickTile?.();

    // 3. 標記出牌中 → scene 重畫手牌（排除這張）、隱藏 action bar
    this.pendingDiscardTile = tile;
    this.deps.onSelectionChange();

    // 4. 升起動畫 → 停留 → 出牌
    this.startRise(tile, pos);
  }

  /**
   * 系統驅動出牌（聽牌自動打）：跳過 canSelect 檢查，沿用「升起 → 停留 → 出牌」動畫打出指定牌。
   * 與 handleTileClick 同流程，差別只在不檢查玩家是否可選（由 caller 保證合法時機）。
   */
  triggerDiscard(tile: TTile): void {
    if (this.pendingDiscardTile) return; // 動畫進行中，忽略
    this.clearPrime();
    const pos =
      this.deps.handViews[this.deps.playerSeat]?.getConcealedTilePosition(tile.id) ?? null;
    this.deps.onPickTile?.();
    this.pendingDiscardTile = tile;
    this.deps.onSelectionChange();
    this.startRise(tile, pos);
  }

  /** 點兩下的第一下：在原位上方顯示放大預覽與提示，牌仍留在手上。 */
  private prime(tile: TTile): void {
    this.clearPrime();
    const pos = this.deps.handViews[this.deps.playerSeat]?.getConcealedTilePosition(tile.id);
    if (!pos) return;
    this.primedTile = tile;
    const sprite = new CTileSprite(
      this.deps.scene,
      pos.x,
      pos.y + PRIME_OFFSET_Y,
      tile,
      TileFace.HandBottom
    );
    sprite.setScale(PRIME_SCALE).setDepth(RISE_DEPTH);
    // 點放大的那張也算第二下（Container 沒有預設點擊範圍，另外蓋一個同大小的透明點擊區）
    const bounds = sprite.getBounds();
    const hit = this.deps.scene.add
      .zone(bounds.centerX, bounds.centerY, bounds.width, bounds.height)
      .setDepth(RISE_DEPTH)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => this.handleTileClick(tile));
    const hint = this.deps.scene.add
      .text(pos.x, pos.y + PRIME_OFFSET_Y + PRIME_HINT_OFFSET_Y, '再點一下打出', {
        color: '#fff1cf',
        backgroundColor: '#2a1206',
        fontSize: PRIME_HINT_FONT,
        fontStyle: 'bold',
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5)
      .setDepth(RISE_DEPTH);
    this.primeObjects = [sprite, hit, hint];
  }

  private clearPrime(): void {
    this.primedTile = null;
    for (const object of this.primeObjects) object.destroy();
    this.primeObjects = [];
  }

  /** 清掉動畫狀態（回合被打斷 / 代打接手時呼叫）。出牌已自行收尾者不受影響。 */
  clear(): void {
    this.clearPrime();
    const had = this.pendingDiscardTile !== null;
    this.pendingDiscardTile = null;
    if (this.holdTimer) {
      this.holdTimer.remove(false);
      this.holdTimer = null;
    }
    if (this.riseSprite) {
      this.deps.scene.tweens.killTweensOf(this.riseSprite);
      this.riseSprite.destroy();
      this.riseSprite = null;
    }
    if (had) this.deps.onSelectionChange();
  }

  /** 在手牌原位生成放大牌往上升，停留後觸發出牌。 */
  private startRise(tile: TTile, pos: { x: number; y: number } | null): void {
    const layout = SEAT_LAYOUT[this.deps.playerSeat];
    const handScale = layout.position === 'bottom' ? layout.handScale : RISE_SCALE;
    const handCenterY = layout.position === 'bottom' ? layout.handCenterY : LOGICAL_HEIGHT - 70;
    const startX = pos ? pos.x : LOGICAL_WIDTH / 2;
    const startY = pos ? pos.y : handCenterY;

    const sprite = new CTileSprite(this.deps.scene, startX, startY, tile, TileFace.HandBottom);
    sprite.setScale(handScale);
    sprite.setDepth(RISE_DEPTH); // 蓋過莊家「莊」字 icon，升起的牌不被遮住
    this.riseSprite = sprite;

    // 升起：往上移 + 稍微放大
    this.deps.scene.tweens.add({
      targets: sprite,
      y: startY + RISE_OFFSET_Y,
      scale: RISE_SCALE,
      duration: RISE_DURATION_MS,
      ease: RISE_EASE,
    });

    // 升起完成後停留 DISCARD_HOLD_MS 才出牌
    this.holdTimer = this.deps.scene.time.delayedCall(RISE_DURATION_MS + DISCARD_HOLD_MS, () =>
      this.commitDiscard(tile)
    );
  }

  /** 停留結束：放大牌淡出 + 觸發實際出牌（等待反應或進海底由 scene 接手）。 */
  private commitDiscard(tile: TTile): void {
    this.holdTimer = null;
    const sprite = this.riseSprite;
    // 先把欄位清空（不走 clear()）：onConfirm 會同步引發 tile.discarded → scene 呼 clear()，
    // 此時 pending 已 null，clear() 不會把正在淡出的那張 sprite 砍掉。
    this.pendingDiscardTile = null;
    this.riseSprite = null;
    if (sprite) {
      this.deps.scene.tweens.add({
        targets: sprite,
        alpha: 0,
        duration: FADE_OUT_MS,
        ease: 'Linear',
        onComplete: () => sprite.destroy(),
      });
    }
    this.deps.onConfirm(tile);
  }
}
